// KAMPÁNY-ELŐREJELZÉS — egyetlen nézet: IDŐSÁV.
//
// MIÉRT NINCS LISTA: a lap kérdése nem „mely kampányok vannak", hanem „mikor, kinél, mi fut egyszerre" —
// erre a sávos nézet válaszol, a táblázat csak ugyanazt az adatot mondta el rosszabbul (user-döntés).
// A régi `?view=` paramétert a search-séma csendben elejti, a régi linkek így nem törnek el.
//
// A NÉZET a látható ABLAKRA kérdez (`from`/`to`) — a nagyítás és a léptetés az URL-ben van, tehát a
// „nézd meg a novemberi idősávot" link megosztható.
//
// JOG-KAPU (olvasás-út): nincs terelés — `enabled: !gated` + `ListErrorState forbidden`. A `campaign_forecast.view`
// hiánya a menüpontot is elrejti, ez a lap csak a közvetlen link ellen véd.
import type { CampaignDetail, CampaignRow } from '@atlas/contracts';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CampaignFormSheet } from '@/components/campaign-forecast/campaign-form-sheet';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { CAMPAIGN_STATUSES, campaignKeys } from '@/features/campaign-forecast/api';
import { CampaignTimeline } from '@/features/campaign-forecast/components/campaign-timeline';
import {
  periodLabel,
  shiftAnchor,
  windowFor,
  type TimelineZoom,
} from '@/features/campaign-forecast/timeline';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { requireAuth } from '@/lib/auth';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

const campaignSearchSchema = z.object({
  q: z.string().optional(),
  clientId: z.string().optional(),
  accountId: z.string().optional(),
  ownerId: z.string().optional(),
  status: z.string().optional(),
  zoom: z.string().optional(),
  anchor: z.string().optional(),
  edit: z.string().optional(),
});

export const Route = createFileRoute('/campaign-forecast')({
  validateSearch: (search) => campaignSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: CampaignForecastRoute,
});

const ALL = 'all';
/** Az idősáv EGY ablakra kérdez — a felső korlát a busz-lista maximuma. */
const TIMELINE_LIMIT = 200;

function CampaignForecastRoute(): JSX.Element {
  const { edit } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/campaign-forecast',
      search: (prev) => ({ ...prev, edit: undefined }),
      replace: true,
    });
  }, [navigate]);
  return <CampaignForecastPage deepLinkEditId={edit} onClearDeepLink={clearDeepLink} />;
}

export interface CampaignForecastPageProps {
  deepLinkEditId?: string | undefined;
  onClearDeepLink?: () => void;
  /** A „ma" injektálható — a route-teszt így determinisztikus (a nézet nem függ a futtatás napjától). */
  today?: string;
}

export function CampaignForecastPage({
  deepLinkEditId,
  onClearDeepLink,
  today: todayProp,
}: CampaignForecastPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('campaign_forecast.view');
  const canManage = can('campaign_forecast.manage');
  const canDelete = can('campaign_forecast.delete');

  const today = useMemo(
    () =>
      todayProp ??
      new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Budapest',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date()),
    [todayProp],
  );

  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    clientId: ALL,
    accountId: ALL,
    ownerId: ALL,
    status: ALL,
    zoom: 'month',
    anchor: '',
  });
  const q = String(urlFilters.q);
  const clientId = String(urlFilters.clientId);
  const accountId = String(urlFilters.accountId);
  const ownerId = String(urlFilters.ownerId);
  const status = String(urlFilters.status);
  // Az ALAP a HÓNAP (user-döntés): a kampány-ablak jellemzően hetekben mozog, a negyedév első ránézésre
  // túl sok sort zsúfolt egy képernyőre. A `?zoom=` deep-link mindhárom értéket viszi.
  const urlZoom: TimelineZoom =
    urlFilters.zoom === 'quarter' || urlFilters.zoom === 'year' ? urlFilters.zoom : 'month';
  /**
   * MOBILON FIX HAVI (user-döntés) — a negyedév/év 375 px-en olvashatatlanul sűrű sávokat adna, a
   * nagyítás-váltó pedig kicsordulna. A naptár ugyanígy szűkít telefonon (nincs hónap-nézet, nincs „ma").
   *
   * A HATÁLYOS érték megy MINDENHOVA (ablak, léptetés, felirat, idősáv) — enélkül a `‹ ›` egy ÉVET
   * léptetne, miközben a felirat hónapot mutat. Az URL-t viszont SZÁNDÉKOSAN nem írjuk át: egy
   * `?zoom=year` deep-link telefonon havi nézetben nyílik, de asztali gépen/elforgatás után visszakapja
   * a felhasználó, amit kért.
   */
  const isMobile = useIsMobile();
  const zoom: TimelineZoom = isMobile ? 'month' : urlZoom;
  const anchor = String(urlFilters.anchor) === '' ? today : String(urlFilters.anchor);
  const timelineWindow = useMemo(() => windowFor(zoom, anchor), [zoom, anchor]);

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(deepLinkEditId ?? null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string } | null>(null);

  useEffect(() => {
    if (deepLinkEditId !== undefined) setEditId(deepLinkEditId);
  }, [deepLinkEditId]);

  const gated = !permsLoading && !canView;

  // ── az IDŐSÁV lekérdezése: a látható ABLAKRA, a szűrőkkel együtt ──
  const timelineKey = [
    ...campaignKeys.timeline(timelineWindow),
    { q, clientId, accountId, ownerId, status },
  ];
  const timelineQuery = useQuery({
    queryKey: timelineKey,
    enabled: !gated,
    queryFn: () =>
      api.campaignForecast.list.query({
        query: {
          ...(q === '' ? {} : { q }),
          ...(clientId === ALL ? {} : { clientId }),
          ...(accountId === ALL ? {} : { accountId }),
          ...(ownerId === ALL ? {} : { ownerId }),
          ...(status === ALL ? {} : { status: status as never }),
          from: timelineWindow.from,
          to: timelineWindow.to,
          limit: TIMELINE_LIMIT,
        },
      }),
  });

  const body = timelineQuery.data?.status === 200 ? timelineQuery.data.body : null;
  const rows = body?.campaigns ?? [];
  const isForbidden = gated || isForbiddenStatus(timelineQuery.data?.status);
  const isError =
    gated || timelineQuery.isError || (timelineQuery.data !== undefined && body === null);
  const isLoading = !gated && (permsLoading || timelineQuery.isLoading);

  // ── a form FORRÁSAI (csak `manage` joggal, és csak ha kell) ──
  const refsKey = campaignKeys.refs(locale);
  const refsQuery = useQuery({
    queryKey: refsKey,
    enabled: canManage && (createOpen || editId !== null),
    // A refs a TELJES ügyfél/fiók/személy-katalógust lapozza (mérve: 7 upstream-lap). A katalógus ritkán
    // változik, a szerkesztő viszont gyakran nyílik — friss lekérés minden nyitáskor felesleges terhelés az
    // Odinon. Öt percig élőnek tekintjük; új fiók felvétele után a lap újratöltése hozza.
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      api.campaignForecast.refs.query({ query: { lang: locale === 'en' ? 'en' : 'hu' } }),
  });
  const refs = refsQuery.data?.status === 200 ? refsQuery.data.body : undefined;
  // A form-források ÜZEM-ZAVARA (502) nem maradhat néma: a sheet kimondja, és a mentést letiltja.
  const refsFailed = refsQuery.isError || (refsQuery.data !== undefined && refs === undefined);

  const detailKey = campaignKeys.detail(editId ?? 'none');
  const detailQuery = api.campaignForecast.detail.useQuery(
    detailKey,
    { params: { id: editId ?? 'none' } },
    { queryKey: detailKey, enabled: editId !== null },
  );
  const editItem: CampaignDetail | null =
    detailQuery.data?.status === 200 ? detailQuery.data.body : null;

  const closeEdit = useCallback(() => {
    setEditId(null);
    onClearDeepLink?.();
  }, [onClearDeepLink]);

  const removeMutation = useEntityMutation<{ id: string }, unknown>({
    mutationFn: ({ id }) => api.campaignForecast.remove.mutation({ params: { id } }),
    invalidateKeys: [campaignKeys.all],
    // OPTIMISTA TÖRLÉS: a sáv AZONNAL eltűnik az idősávról, hibára visszagördül (a repo alap-konvenciója).
    // A darabszám is a sorokból jön, ezért a `total`-t is csökkentjük — különben „24 / 25" villanna a
    // visszagördülésig.
    optimistic: {
      queryKey: timelineKey,
      update: (old) => {
        const snapshot = old as {
          status: number;
          body: { campaigns: CampaignRow[]; total: number };
        };
        if (deleteTarget === null || snapshot.status !== 200) return old;
        return {
          ...snapshot,
          body: {
            ...snapshot.body,
            campaigns: snapshot.body.campaigns.filter((c) => c.id !== deleteTarget.id),
            total: Math.max(0, snapshot.body.total - 1),
          },
        };
      },
    },
    messages: {
      success: t('toast:campaignForecast.deleted'),
      conflict: t('toast:campaignForecast.conflict'),
      forbidden: t('toast:campaignForecast.forbidden'),
      validation: t('toast:campaignForecast.validation'),
      error: t('toast:campaignForecast.deleteError'),
    },
  });
  // A törlést a SZERKESZTŐ indítja (lista híján ez az egyetlen sor-művelet-hely). A megerősítéssel a sheet
  // AZONNAL zárul — ugyanazzal a mozdulattal, amivel a sáv eltűnik: egy már törölt kampány nyitva hagyott
  // űrlapja fölött a felhasználó az üres idősávot nézné. Hibára a sáv visszagördül, a toast pedig kimondja.
  const confirmDelete = useCallback(() => {
    if (deleteTarget) {
      closeEdit();
      removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
    }
  }, [deleteTarget, removeMutation, closeEdit]);

  return (
    <AppShell
      // Mobilon a lebegő FAB (primaryAction) kezeli a létrehozást — csak ha van manage-jog. Desktopon a
      // toolbar `ListNewButton`-ja (lásd lent, `hidden md:block`). Enélkül telefonon SEHOGY nem lehetett
      // kampányt felvenni: a gomb `hidden md:block`, FAB pedig nem volt — más lista-oldalak adnak.
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('campaignForecast.new'),
              onClick: () => setCreateOpen(true),
            }
          : undefined
      }
      toolbar={
        <div className="flex flex-col">
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            filterItems={[
              {
                kind: 'select',
                key: 'status',
                label: t('campaignForecast.filters.status'),
                value: status,
                onChange: (v: string) => setFilters({ status: v }),
                options: CAMPAIGN_STATUSES.map((s) => ({
                  value: s,
                  label: t(`campaignForecast.status.${s}`),
                })),
                allValue: ALL,
              },
            ]}
            meta={
              timelineQuery.isFetching
                ? t('campaignForecast.loading')
                : !isError
                  ? t('campaignForecast.countOf', { shown: rows.length, total: body?.total ?? 0 })
                  : ''
            }
            actions={
              <div className="hidden md:block">
                <ListNewButton
                  label={t('campaignForecast.new')}
                  onClick={() => setCreateOpen(true)}
                  enabled={canManage}
                  disabledTooltip={t('common.noCreatePermission')}
                />
              </div>
            }
          />

          {/* NAVIGÁCIÓS SOR — a megbeszélés-naptár fejlécének PONTOS mása (user-döntés: „egységes legyen").
              A méretek MÉRVE, nem becsülve (`.fc-toolbar`, 1280 px): minden gomb 28 px magas, 12 px / 500-as
              felirattal; a léptető-pár ÖSSZEFŰZVE (a második −1 px-rel csúszik a szomszédjára, hogy egy
              szegély legyen közöttük), a „ma" külön áll; az időszak-felirat 16 px / 600; a sáv 12 px-re ül a
              kereső alatt és 12 px-et hagy maga alatt. FELSŐ margó NINCS: azt a `ListToolbar` saját alsó
              térköze adja — `mt-3`-mal 24 px lett volna (mérve).

              MIÉRT KÖZÉPEN AZ IDŐSZAK, és nem a jelmagyarázat: a léptető gombok önmagukban vakon mozognak —
              a lapon eddig SEHOL nem látszott, melyik időszakot nézed (az idősávon csak tengely-osztások
              vannak). A jelmagyarázat az idősáv ALATT marad, a diagram mellett — ott a helye.

              A `grid-cols-[1fr_auto_1fr]` azért kell flex helyett, hogy a középső felirat a SÁV közepén üljön,
              ne a két oldalsó blokk méretétől függő helyen. KESKENY sávon (a `md` alatt) viszont sima flex: a
              három elem egy sorban elfér, a felirat pedig nem a sáv közepére, hanem a két szélső blokk közé
              kerül. A harmadik blokkot ilyenkor `ml-auto` viszi a JOBB SZÉLRE — enélkül a felirathoz tapadna
              (a felhasználó pont ezt jelezte mobilnézetben). */}
          <div className="mb-3 flex flex-wrap items-center gap-2 px-4 md:grid md:grid-cols-[1fr_auto_1fr]">
            <div className="flex items-center">
              {/* ÖSSZEFŰZÖTT léptető-pár — a naptár `fc-button-group`-jának megfelelője. */}
              <Button
                variant="outline"
                size="sm"
                aria-label={t('campaignForecast.zoom.previous')}
                className="h-7 w-[38px] rounded-r-none px-0"
                onClick={() => setFilters({ anchor: shiftAnchor(zoom, anchor, -1) })}
              >
                <ChevronLeftIcon className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-label={t('campaignForecast.zoom.next')}
                className="-ml-px h-7 w-[38px] rounded-l-none px-0"
                onClick={() => setFilters({ anchor: shiftAnchor(zoom, anchor, 1) })}
              >
                <ChevronRightIcon className="size-4" />
              </Button>
              {/* A „ma" CSAK asztali gépen — telefonon a naptár is elhagyja (szűk a sáv, és a lap amúgy is a
                  mai hónapon nyílik). */}
              {!isMobile && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-2.5 h-7"
                  onClick={() => setFilters({ anchor: today })}
                >
                  {t('campaignForecast.zoom.today')}
                </Button>
              )}
            </div>

            <Text variant="title" as="div" className="text-center text-lg font-semibold">
              {periodLabel(zoom, anchor, i18n.language, t('campaignForecast.zoom.quarterWord'))}
            </Text>

            {/* NAGYÍTÁS-VÁLTÓ. Telefonon fix havi a nézet (l. a `zoom` levezetését), ezért ott CSAK a
                „hónap" jelölés marad — a negyedév/év 375 px-en olvashatatlanul sűrű sávokat adna, és a három
                gomb kicsordult vagy függőlegesen tördelődött (mérve).

                MOBILON NEM `<button>`, hanem sima elem a gomb MEGJELENÉSÉVEL: úgy NÉZ KI, mint az aktív
                szegmens, de nem is fókuszálható és nem is hirdeti magát vezérlőnek. Egy `<button disabled>`
                azt mondaná a képernyőolvasónak, hogy „letiltott gomb" — pedig itt nincs mit engedélyezni: ez
                az egyetlen lehetséges nézet, tehát ÁLLAPOT-jelölés, nem elromlott kapcsoló.

                Asztali gépen összefűzött gomb-csoport, NEM a `Segmented` primitív: az utóbbi padding-es
                konténerbe ágyazott, „kiemelkedő" aktív lapot rajzol (36 px magas) — a naptáré ezzel szemben
                három, egymáshoz fűzött 28 px-es gomb, az aktívon accent-tintával és accent-szegéllyel. Itt a
                naptárral kell egyeznie, ezért annak a megjelenését vesszük át, ugyanazokkal a tokenekkel. */}
            {isMobile ? (
              <div
                className={cn(
                  // a `Button variant="outline" size="sm"` geometriája, az AKTÍV szegmens színeivel
                  'border-accent-500 bg-accent-500/[0.18] text-foreground',
                  'inline-flex h-7 select-none items-center rounded-md border px-2.5 text-sm font-medium',
                  // a SÁV JOBB SZÉLÉRE, ahová asztali gépen a nagyítás-váltó kerül (l. a konténer megjegyzését)
                  'ml-auto',
                )}
              >
                {t('campaignForecast.zoom.month')}
              </div>
            ) : (
              <div
                // `ml-auto`: a 641–767 px-es sávban a konténer még FLEX (a grid csak `md`-től él), ott a
                // `justify-end` önmagában nem visz a jobb szélre — a blokk a felirat mellé tapadna.
                className="ml-auto flex justify-end md:ml-0"
                role="radiogroup"
                aria-label={t('campaignForecast.zoom.label')}
              >
                {(['month', 'quarter', 'year'] as const).map((z, i) => (
                  <Button
                    key={z}
                    variant="outline"
                    size="sm"
                    role="radio"
                    aria-checked={zoom === z}
                    onClick={() => setFilters({ zoom: z })}
                    className={cn(
                      'h-7',
                      i > 0 && '-ml-px',
                      i === 0 && 'rounded-r-none',
                      i === 1 && 'rounded-none',
                      i === 2 && 'rounded-l-none',
                      // az aktív szegmens a szomszédja szegélye FÖLÉ kerül (különben a −1px elfedné)
                      zoom === z &&
                        'border-accent-500 bg-accent-500/[0.18] text-foreground hover:bg-accent-500/[0.18] relative z-[1]',
                    )}
                  >
                    {t(`campaignForecast.zoom.${z}`)}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="flex h-full flex-col">
        <div className="flex min-h-0 flex-1 flex-col px-4 pb-2">
          {isError ? (
            <ListErrorState forbidden={isForbidden} onRetry={() => void timelineQuery.refetch()} />
          ) : (
            <CampaignTimeline
              rows={rows}
              window={timelineWindow}
              zoom={zoom}
              today={today}
              isLoading={isLoading}
              onSelect={(id) => {
                if (canManage) setEditId(id);
              }}
            />
          )}
        </div>
      </div>

      <CampaignFormSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        item={null}
        refs={refs}
        refsFailed={refsFailed}
      />
      <CampaignFormSheet
        open={editId !== null && editItem !== null}
        onOpenChange={(open) => {
          if (!open) closeEdit();
        }}
        item={editItem}
        refs={refs}
        refsFailed={refsFailed}
        {...(canDelete
          ? {
              onDelete: () => {
                if (editItem !== null) setDeleteTarget({ id: editItem.id });
              },
            }
          : {})}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('campaignForecast.deleteTitle')}
        description={t('campaignForecast.deleteDesc')}
        confirmLabel={
          removeMutation.isPending ? t('campaignForecast.deleting') : t('campaignForecast.delete')
        }
        cancelLabel={t('campaignForecast.cancel')}
        onConfirm={confirmDelete}
        destructive
      />
    </AppShell>
  );
}
